import React, { useState, useEffect, useMemo } from "react";
import {
  X,
  Trash2,
  Upload,
  Camera,
  AlertCircle,
  Plus,
  Check,
  AlertTriangle,
} from "lucide-react";
import { db } from "../services/firebase";
import { doc, updateDoc, deleteDoc } from "firebase/firestore";
import { getProductOptions } from "../pages/SellerSegment";
import "./EditRejectedProductModal.css";

const CLOUDINARY_CLOUD_NAME =
  process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "dosrgvlaf";
const CLOUDINARY_UPLOAD_PRESET =
  process.env.REACT_APP_CLOUDINARY_CLOUD_PRESET || "jmiseller";
const CLOUDINARY_API_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;

const parseMOQ = (mq) => {
  if (typeof mq !== "string") return parseFloat(mq) || 0;
  const numParts = mq.match(/(\d+)(?:\*(\d+))?|(\d+)(?:PC)?|Min\s+(\d+)/);
  if (numParts) {
    const [, base, multiplier, pc, min] = numParts;
    if (base)
      return parseFloat(base) * (multiplier ? parseFloat(multiplier) : 1);
    if (pc) return parseFloat(pc);
    if (min) return parseFloat(min);
  }
  return parseFloat(mq) || 0;
};

const normalizeImage = (img, index) => {
  if (!img) return null;
  if (typeof img === "string") {
    return {
      id: `img-${index}-${Date.now()}`,
      url: img.trim(),
      name: `image-${index + 1}`,
    };
  }
  if (typeof img === "object" && img.url) {
    return {
      id: img.id || `img-${index}-${Date.now()}`,
      url: img.url.trim(),
      name: img.name || `image-${index + 1}`,
    };
  }
  return null;
};

const EditRejectedProductModal = ({
  isOpen,
  onClose,
  product,
  sellerProductData = {},
  onProductUpdated,
  onProductDeleted,
}) => {
  const [images, setImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const [moqSet, setMoqSet] = useState("");
  const [serviceType, setServiceType] = useState("ready");

  const [lotSizes, setLotSizes] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [validationError, setValidationError] = useState("");

  // Initialize data when product changes or modal opens
  useEffect(() => {
    if (!product) return;

    setServiceType(product.serviceType || "ready");
    setMoqSet(product.moqSet?.toString() || "");

    // Initialize images
    const rawImages = Array.isArray(product.images) ? product.images : [];
    const normalized = rawImages
      .map((img, i) => normalizeImage(img, i))
      .filter(Boolean);
    setImages(normalized);
    setSelectedImageIndex(0);

    if (Array.isArray(product.lotDetails) && product.lotDetails.length > 0) {
      setLotSizes(
        product.lotDetails.map((item) => ({
          size: item.size || "",
          set:
            item.set?.toString() ||
            (product.serviceType === "ready" ? "1" : "0"),
          grossWt: item.grossWt?.toString() || "",
          netWt: item.netWt?.toString() || "",
          avgGrossWt: item.avgGrossWt?.toString() || "",
          avgNetWt: item.avgNetWt?.toString() || "",
          avgSpecWt: item.avgSpecWt?.toString() || "",
        })),
      );
    } else {
      const defaultSize = Array.isArray(product.sizes)
        ? product.sizes[0] || ""
        : product.sizes || "";
      setLotSizes([
        {
          size: defaultSize,
          set:
            product.instockSet?.toString() ||
            (product.serviceType === "ready" ? "1" : "0"),
          grossWt: product.grossWt?.toString() || "",
          netWt: product.netWt?.toString() || "",
          avgGrossWt: "",
          avgNetWt: "",
          avgSpecWt: "",
        },
      ]);
    }

    setFieldErrors({});
    setValidationError("");
    setUploadError("");
    setShowDeleteConfirm(false);
  }, [product, isOpen]);

  const isPlane = useMemo(() => {
    const spec = (product?.specification || "").toUpperCase();
    return spec === "PLANE" || spec === "PLAIN" || !spec;
  }, [product?.specification]);

  // Size options from sellerProductData
  const sizeOptions = useMemo(() => {
    if (!product) return [];
    const segment = product.segment?.toUpperCase();
    const category = product.category;
    const source = product.productSource;
    const productName = product.productName?.toUpperCase();

    let options = getProductOptions(segment, category, source, productName);

    if (options.length === 0) {
      // Fallback options
      const existingSizes = new Set();
      if (Array.isArray(product.sizes)) {
        product.sizes.forEach((s) => existingSizes.add(s));
      } else if (typeof product.sizes === "string" && product.sizes) {
        existingSizes.add(product.sizes);
      }
      if (Array.isArray(product.lotDetails)) {
        product.lotDetails.forEach((ld) => {
          if (ld.size) existingSizes.add(ld.size);
        });
      }
      if (existingSizes.size > 0) {
        options = Array.from(existingSizes);
      } else {
        options = [
          "2-2",
          "2-4",
          "2-6",
          "2-8",
          "Free Size",
          "16",
          "18",
          "20",
          "Standard",
        ];
      }
    }

    return options;
  }, [product]);

  // Handle MOQ change with real-time validation against size rows
  const handleMoqChange = (value) => {
    setMoqSet(value);
    const parsedMoq = parseMOQ(value);
    if (serviceType === "ready" && parsedMoq > 0) {
      setFieldErrors((prev) => {
        const nextErrors = { ...prev };
        lotSizes.forEach((item, idx) => {
          const s = parseFloat(item.set) || 0;
          if (s > 0 && s < parsedMoq) {
            nextErrors[`${idx}-set`] = `Set must be >= MOQ (${parsedMoq})`;
          } else {
            delete nextErrors[`${idx}-set`];
          }
        });
        return nextErrors;
      });
    }
  };

  // Handle lot size row changes like upload product lot drawer
  const handleSizeRowChange = (index, field, value) => {
    const parsedMoq = parseMOQ(moqSet);

    setLotSizes((prev) => {
      const updated = [...prev];
      const item = { ...updated[index], [field]: value };

      if (serviceType === "ready") {
        // Validate set >= MOQ
        if (field === "set") {
          const s = parseFloat(value) || 0;
          if (parsedMoq > 0 && s < parsedMoq) {
            setFieldErrors((errs) => ({
              ...errs,
              [`${index}-set`]: `Set must be >= MOQ (${parsedMoq})`,
            }));
          } else {
            setFieldErrors((errs) => {
              const copy = { ...errs };
              delete copy[`${index}-set`];
              return copy;
            });
          }
        }

        // Validate Net Wt <= Gross Wt
        if (field === "netWt" || field === "grossWt") {
          const g = parseFloat(field === "grossWt" ? value : item.grossWt) || 0;
          const n = parseFloat(field === "netWt" ? value : item.netWt) || 0;
          if (g > 0 && n > g) {
            setFieldErrors((errs) => ({
              ...errs,
              [`${index}-netWt`]: "Net Wt cannot be > Gross Wt",
            }));
          } else {
            setFieldErrors((errs) => {
              const copy = { ...errs };
              delete copy[`${index}-netWt`];
              return copy;
            });
          }
        }

        const setVal = parseFloat(item.set) || 0;
        const grossVal = parseFloat(item.grossWt) || 0;
        const netVal = parseFloat(item.netWt) || 0;

        if (setVal > 0) {
          item.avgGrossWt = (grossVal / setVal).toFixed(3);
          item.avgNetWt = (netVal / setVal).toFixed(3);
          item.avgSpecWt = !isPlane
            ? Math.max(0, (grossVal - netVal) / setVal).toFixed(3)
            : "0.000";
        } else {
          item.avgGrossWt = "0.000";
          item.avgNetWt = "0.000";
          item.avgSpecWt = "0.000";
        }
      } else {
        // Order serve mode
        if (field === "avgNetWt" || field === "avgGrossWt") {
          const aG =
            parseFloat(field === "avgGrossWt" ? value : item.avgGrossWt) || 0;
          const aN =
            parseFloat(field === "avgNetWt" ? value : item.avgNetWt) || 0;
          if (aG > 0 && aN > aG) {
            setFieldErrors((errs) => ({
              ...errs,
              [`${index}-avgNetWt`]: "Net Wt/Set cannot be > Gross Wt/Set",
            }));
          } else {
            setFieldErrors((errs) => {
              const copy = { ...errs };
              delete copy[`${index}-avgNetWt`];
              return copy;
            });
          }
        }

        const avgGross = parseFloat(item.avgGrossWt) || 0;
        const avgNet = parseFloat(item.avgNetWt) || 0;
        item.avgSpecWt = !isPlane
          ? Math.max(0, avgGross - avgNet).toFixed(3)
          : "0.000";
      }

      updated[index] = item;
      return updated;
    });
  };

  // Add a new size row
  const handleAddSizeRow = () => {
    const availableSizes = sizeOptions.filter(
      (size) => !lotSizes.some((item) => item.size === size),
    );
    if (availableSizes.length === 0) {
      alert("All available sizes have been added.");
      return;
    }
    const nextSize = availableSizes[0];
    const parsedMoq = parseMOQ(moqSet);

    setLotSizes((prev) => [
      ...prev,
      {
        size: nextSize,
        set:
          serviceType === "ready"
            ? parsedMoq > 0
              ? parsedMoq.toString()
              : "1"
            : "0",
        grossWt: "",
        netWt: "",
        avgGrossWt: "",
        avgNetWt: "",
        avgSpecWt: "",
      },
    ]);
  };

  // Remove a size row
  const handleRemoveSizeRow = (index) => {
    if (lotSizes.length <= 1) return;
    setLotSizes((prev) => prev.filter((_, i) => i !== index));
    setFieldErrors((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        const [rowIdx, field] = k.split("-");
        const idxNum = parseInt(rowIdx, 10);
        if (idxNum < index) next[k] = prev[k];
        else if (idxNum > index) next[`${idxNum - 1}-${field}`] = prev[k];
      });
      return next;
    });
  };

  // Calculate totals & averages
  const totals = useMemo(() => {
    let totalSets = 0;
    let totalGrossWt = 0;
    let totalNetWt = 0;

    lotSizes.forEach((item) => {
      if (serviceType === "order") {
        totalGrossWt += parseFloat(item.avgGrossWt) || 0;
        totalNetWt += parseFloat(item.avgNetWt) || 0;
      } else {
        totalSets += parseFloat(item.set) || 0;
        totalGrossWt += parseFloat(item.grossWt) || 0;
        totalNetWt += parseFloat(item.netWt) || 0;
      }
    });

    const totalSpecWt = !isPlane ? Math.max(0, totalGrossWt - totalNetWt) : 0;

    return {
      totalSets,
      totalGrossWt,
      totalNetWt,
      totalSpecWt,
    };
  }, [lotSizes, serviceType, isPlane]);

  const validateSquareImage = (file) => {
    return new Promise((resolve) => {
      if (!file.type.startsWith("image/")) {
        resolve({
          valid: false,
          file,
          error: `"${file.name}" is not an image file.`,
        });
        return;
      }

      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        const ratio = width / height;
        const isOneToOne = Math.abs(ratio - 1) <= 0.01;

        if (isOneToOne) {
          resolve({ valid: true, file, width, height });
        } else {
          resolve({
            valid: false,
            file,
            width,
            height,
            error: `"${file.name}" is ${width}x${height} (${ratio.toFixed(
              2,
            )}:1). Only 1:1 (square) images are allowed.`,
          });
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({
          valid: false,
          file,
          error: `Failed to load image "${file.name}".`,
        });
      };

      img.src = objectUrl;
    });
  };

  // Image Upload handler
  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploadError("");

    const fileList = Array.from(files);
    const validations = await Promise.all(fileList.map(validateSquareImage));
    const invalid = validations.filter((v) => !v.valid);
    const valid = validations.filter((v) => v.valid);

    if (invalid.length > 0) {
      const errorList = invalid.map((item) => `• ${item.error}`).join("\n");
      setUploadError(`Only 1:1 (square) images are allowed:\n${errorList}`);
      alert(`Only 1:1 (square) images are allowed!\n\n${errorList}`);
    }

    if (valid.length === 0) {
      return;
    }

    setUploadingImages(true);

    try {
      const uploadPromises = valid.map(async ({ file }) => {
        const formDataCloud = new FormData();
        formDataCloud.append("file", file);
        formDataCloud.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
        const response = await fetch(CLOUDINARY_API_URL, {
          method: "POST",
          body: formDataCloud,
        });
        const data = await response.json();
        if (data.secure_url) {
          return {
            id: data.public_id || `img-${Date.now()}-${Math.random()}`,
            url: data.secure_url,
            name: file.name,
          };
        }
        throw new Error(data.error?.message || "Failed to upload image");
      });

      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter(Boolean);
      setImages((prev) => [...prev, ...successfulUploads]);
    } catch (err) {
      console.error("Cloudinary upload failed:", err);
      setUploadError(
        err.message || "Failed to upload image. Please check your network.",
      );
    } finally {
      setUploadingImages(false);
    }
  };

  // Remove image
  const handleRemoveImage = (imgId, e) => {
    e?.stopPropagation();
    setImages((prev) => {
      const filtered = prev.filter((img) => img.id !== imgId);
      if (selectedImageIndex >= filtered.length) {
        setSelectedImageIndex(Math.max(0, filtered.length - 1));
      }
      return filtered;
    });
  };

  // Delete Product handler
  const handleDeleteProduct = async () => {
    if (!product?.id) return;
    setIsDeleting(true);
    try {
      await deleteDoc(doc(db, "products", product.id));
      alert("Product deleted successfully.");
      if (onProductDeleted) {
        onProductDeleted(product.id);
      }
      onClose();
    } catch (err) {
      console.error("Error deleting product:", err);
      alert("Failed to delete product: " + err.message);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Save & Resubmit handler with full MOQ and Lot validation like product upload
  const handleSaveAndResubmit = async () => {
    setValidationError("");

    if (images.length === 0) {
      setValidationError("Please keep or upload at least 1 product image.");
      return;
    }

    const parsedMoq = parseMOQ(moqSet);
    if (!moqSet || parsedMoq <= 0) {
      setValidationError("Please enter a valid MOQ Set (greater than 0).");
      return;
    }

    if (lotSizes.length === 0) {
      setValidationError("Please specify at least one size row.");
      return;
    }

    for (let i = 0; i < lotSizes.length; i++) {
      const row = lotSizes[i];
      const sizeName = row.size || `Row #${i + 1}`;
      if (!row.size) {
        setValidationError(`Please select a size for row #${i + 1}.`);
        return;
      }

      if (serviceType === "ready") {
        const s = parseFloat(row.set);
        const g = parseFloat(row.grossWt);
        const n = parseFloat(row.netWt);

        if (isNaN(s) || s <= 0) {
          setValidationError(
            `Please enter a valid Set count (> 0) for size "${sizeName}".`,
          );
          return;
        }

        // Validate each size set must be >= MOQ like in product upload
        if (s < parsedMoq) {
          setValidationError(
            `MOQ (${parsedMoq}) not met in size "${sizeName}". Each size set (${s}) must be >= MOQ (${parsedMoq}).`,
          );
          return;
        }

        if (isNaN(g) || g <= 0) {
          setValidationError(
            `Please enter a valid Gross Wt (> 0) for size "${sizeName}".`,
          );
          return;
        }

        if (isNaN(n) || n <= 0) {
          setValidationError(
            `Please enter a valid Net Wt (> 0) for size "${sizeName}".`,
          );
          return;
        }

        if (n > g) {
          setValidationError(
            `Net Wt cannot be greater than Gross Wt for size "${sizeName}".`,
          );
          return;
        }
      } else {
        const ag = parseFloat(row.avgGrossWt);
        const an = parseFloat(row.avgNetWt);

        if (isNaN(ag) || ag <= 0) {
          setValidationError(
            `Please enter valid Gross Wt / Set for size "${sizeName}".`,
          );
          return;
        }

        if (isNaN(an) || an <= 0) {
          setValidationError(
            `Please enter valid Net Wt / Set for size "${sizeName}".`,
          );
          return;
        }

        if (an > ag) {
          setValidationError(
            `Net Wt cannot exceed Gross Wt for size "${sizeName}".`,
          );
          return;
        }
      }
    }

    // Validate MOQ <= total sets in ready stock
    if (serviceType === "ready" && parsedMoq > totals.totalSets) {
      setValidationError(
        `MOQ Set (${parsedMoq}) cannot be greater than Total Sets (${totals.totalSets}).`,
      );
      return;
    }

    setIsSaving(true);
    try {
      const newSizes = lotSizes.map((item) => item.size).filter(Boolean);
      const specWt = !isPlane ? totals.totalSpecWt.toFixed(3) : "0";
      const isMultiLot = lotSizes.length > 1;

      // Calculated lot sizes with averages guaranteed
      const finalLotSizes = lotSizes.map((item) => {
        const s = parseFloat(item.set) || 0;
        const g = parseFloat(item.grossWt) || 0;
        const n = parseFloat(item.netWt) || 0;
        if (serviceType === "ready") {
          return {
            ...item,
            avgGrossWt: s > 0 ? (g / s).toFixed(3) : "0.000",
            avgNetWt: s > 0 ? (n / s).toFixed(3) : "0.000",
            avgSpecWt:
              s > 0 && !isPlane ? Math.max(0, (g - n) / s).toFixed(3) : "0.000",
          };
        }
        return {
          ...item,
          avgSpecWt: !isPlane
            ? Math.max(
                0,
                (parseFloat(item.avgGrossWt) || 0) -
                  (parseFloat(item.avgNetWt) || 0),
              ).toFixed(3)
            : "0.000",
        };
      });

      const updatePayload = {
        images,
        moqSet: parsedMoq,
        designCode: product?.designCode || "",
        Lot: isMultiLot,
        lotDetails: finalLotSizes,
        lotTotals: totals,
        grossWt: totals.totalGrossWt.toFixed(3),
        netWt: totals.totalNetWt.toFixed(3),
        instockGram: totals.totalGrossWt.toFixed(3),
        instockSet: totals.totalSets.toString(),
        sizes: newSizes,
        specificationWt: specWt,
        sellerPayment:
          product?.sellerPayment || product?.paymentMethod || "RTGS",
        paymentMethod:
          product?.paymentMethod || product?.sellerPayment || "RTGS",
        status: "pending", // Resubmit to QC Pending
        rejectionReason: null,
        reason: null,
        updatedAt: new Date(),
        resubmittedAt: new Date(),
      };

      await updateDoc(doc(db, "products", product.id), updatePayload);
      alert("Product updated and resubmitted for QC approval successfully!");
      if (onProductUpdated) {
        onProductUpdated({ ...product, ...updatePayload });
      }
      onClose();
    } catch (err) {
      console.error("Error updating rejected product:", err);
      setValidationError("Failed to update product: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !product) return null;

  const rejectionReason =
    product.rejectionReason ||
    product.reason ||
    "Product rejected during QC review.";

  return (
    <div className="erp-modal-overlay" onClick={onClose}>
      <div className="erp-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="erp-modal-header">
          <div className="erp-header-title-wrap">
            <span className="erp-badge-rejected">QC Rejected</span>
            <h3>Edit & Resubmit Product</h3>
            <p className="erp-header-subtitle">{product.productName}</p>
          </div>

          <div className="erp-header-actions">
            <button
              type="button"
              className="erp-delete-header-btn"
              onClick={() => setShowDeleteConfirm(true)}
              title="Delete Product"
            >
              <Trash2 size={18} />
              <span>Delete</span>
            </button>
            <button
              type="button"
              className="erp-close-btn"
              onClick={onClose}
              title="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="erp-modal-body">
          {/* Rejection Alert */}
          <div className="erp-rejection-banner">
            <div className="erp-rejection-icon">
              <AlertCircle size={20} />
            </div>
            <div className="erp-rejection-content">
              <strong>QC Rejection Reason</strong>
              <p>{rejectionReason}</p>
            </div>
          </div>

          {/* Validation Error Banner */}
          {validationError && (
            <div className="erp-error-banner">
              <AlertTriangle size={18} />
              <span>{validationError}</span>
            </div>
          )}

          {/* Product Overview Summary */}
          <div className="erp-summary-section">
            <div className="erp-summary-row">
              <span className="erp-summary-tag">
                {product.segment} / {product.category}
              </span>
              <span className="erp-summary-tag">
                {product.productSource} • {product.styleType || "Regular"}
              </span>
              <span className="erp-summary-tag">
                {product.specification || "PLANE"}
              </span>
              {product.purity && (
                <span className="erp-summary-tag highlight">
                  Purity: {product.purity}
                </span>
              )}
              {product.wastage && (
                <span className="erp-summary-tag highlight">
                  Wastage: {product.wastage}%
                </span>
              )}
            </div>
          </div>

          {/* SECTION 1: Product Images */}
          <div className="erp-form-section">
            <div className="erp-section-header">
              <h4>
                Product Photos <span className="erp-required">*</span>
              </h4>
              <span className="erp-hint">
                {images.length} {images.length === 1 ? "photo" : "photos"}{" "}
                uploaded
              </span>
            </div>

            {uploadError && <p className="erp-photo-error">{uploadError}</p>}

            <div className="erp-photos-container">
              {/* Main Preview */}
              <div className="erp-main-photo-box">
                {images.length > 0 ? (
                  <img
                    src={images[selectedImageIndex]?.url}
                    alt="Main Preview"
                    className="erp-main-photo-img"
                    onError={(e) => {
                      e.target.src =
                        "https://via.placeholder.com/400x400?text=No+Image";
                    }}
                  />
                ) : (
                  <div className="erp-photo-placeholder">
                    <Camera size={36} color="#9ca3af" />
                    <span>No photos uploaded</span>
                  </div>
                )}
              </div>

              {/* Thumbnails & Upload Controls */}
              <div className="erp-thumbnails-panel">
                <div className="erp-thumbnails-grid">
                  {images.map((img, idx) => (
                    <div
                      key={img.id || idx}
                      className={`erp-thumbnail ${
                        idx === selectedImageIndex ? "active" : ""
                      }`}
                      onClick={() => setSelectedImageIndex(idx)}
                    >
                      <img src={img.url} alt={`Thumb ${idx + 1}`} />
                      <button
                        type="button"
                        className="erp-remove-photo-btn"
                        onClick={(e) => handleRemoveImage(img.id, e)}
                        title="Remove photo"
                      >
                        <X size={12} />
                      </button>
                      {idx === 0 && (
                        <span className="erp-main-badge">Main</span>
                      )}
                    </div>
                  ))}
                </div>

                {/* Upload Button */}
                <div className="erp-upload-actions">
                  <input
                    type="file"
                    id="erp-photo-upload-input"
                    multiple
                    accept="image/*"
                    disabled={uploadingImages}
                    style={{ display: "none" }}
                    onChange={(e) => {
                      handleImageUpload(e.target.files);
                      e.target.value = "";
                    }}
                  />
                  <label
                    htmlFor="erp-photo-upload-input"
                    className={`erp-upload-btn ${
                      uploadingImages ? "loading" : ""
                    }`}
                  >
                    <Upload size={16} />
                    <span>
                      {uploadingImages
                        ? "Uploading Photos..."
                        : images.length > 0
                        ? "+ Add More Photos"
                        : "Upload Product Photos"}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 2: Minimum Order Quantity (MOQ) */}
          <div className="erp-form-section">
            <div className="erp-section-header">
              <h4>Minimum Order Quantity (MOQ)</h4>
              <span className="erp-hint">
                All size sets below must be &ge; MOQ
              </span>
            </div>

            <div
              className="erp-fields-grid"
              style={{ gridTemplateColumns: "1fr" }}
            >
              <div className="erp-field-group">
                <label className="erp-field-label">
                  MOQ Set <span className="erp-required">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  className="erp-input"
                  placeholder="e.g. 5"
                  value={moqSet}
                  onChange={(e) => handleMoqChange(e.target.value)}
                />
                <span className="erp-field-hint">
                  Required: Every size's set count in lot details must be at
                  least this value
                </span>
              </div>
            </div>
          </div>

          {/* SECTION 3: Lot & Size Details */}
          <div className="erp-form-section">
            <div className="erp-section-header">
              <h4>
                Lot Size Details <span className="erp-required">*</span>
              </h4>
              <span className="erp-hint">
                Mode: {serviceType === "ready" ? "Ready Stock" : "Order Serve"}
              </span>
            </div>

            <div className="erp-lot-table-wrap">
              {lotSizes.map((item, index) => (
                <div key={index} className="erp-lot-row">
                  <div className="erp-lot-col size-col">
                    <label>Size</label>
                    <select
                      value={item.size}
                      onChange={(e) =>
                        handleSizeRowChange(index, "size", e.target.value)
                      }
                      className="erp-input"
                    >
                      <option value="">Select Size</option>
                      {sizeOptions
                        .filter(
                          (opt) =>
                            opt === item.size ||
                            !lotSizes.some((other) => other.size === opt),
                        )
                        .map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                    </select>
                  </div>

                  {serviceType === "ready" ? (
                    <>
                      <div className="erp-lot-col">
                        <label>Total Set</label>
                        <input
                          type="number"
                          min="1"
                          className={`erp-input ${
                            fieldErrors[`${index}-set`] ? "error" : ""
                          }`}
                          value={item.set}
                          onChange={(e) =>
                            handleSizeRowChange(index, "set", e.target.value)
                          }
                        />
                        {fieldErrors[`${index}-set`] && (
                          <span className="erp-field-error">
                            {fieldErrors[`${index}-set`]}
                          </span>
                        )}
                      </div>
                      <div className="erp-lot-col">
                        <label>Total Gross Wt. (g)</label>
                        <input
                          type="number"
                          step="0.001"
                          className="erp-input"
                          value={item.grossWt}
                          onChange={(e) =>
                            handleSizeRowChange(
                              index,
                              "grossWt",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                      <div className="erp-lot-col">
                        <label>Total Net Wt. (g)</label>
                        <input
                          type="number"
                          step="0.001"
                          className={`erp-input ${
                            fieldErrors[`${index}-netWt`] ? "error" : ""
                          }`}
                          value={item.netWt}
                          onChange={(e) =>
                            handleSizeRowChange(index, "netWt", e.target.value)
                          }
                        />
                        {fieldErrors[`${index}-netWt`] && (
                          <span className="erp-field-error">
                            {fieldErrors[`${index}-netWt`]}
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="erp-lot-col">
                        <label>Total Gross Wt / Set (g)</label>
                        <input
                          type="number"
                          step="0.001"
                          className="erp-input"
                          value={item.avgGrossWt}
                          onChange={(e) =>
                            handleSizeRowChange(
                              index,
                              "avgGrossWt",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                      <div className="erp-lot-col">
                        <label>Total Net Wt / Set (g)</label>
                        <input
                          type="number"
                          step="0.001"
                          className={`erp-input ${
                            fieldErrors[`${index}-avgNetWt`] ? "error" : ""
                          }`}
                          value={item.avgNetWt}
                          onChange={(e) =>
                            handleSizeRowChange(
                              index,
                              "avgNetWt",
                              e.target.value,
                            )
                          }
                        />
                        {fieldErrors[`${index}-avgNetWt`] && (
                          <span className="erp-field-error">
                            {fieldErrors[`${index}-avgNetWt`]}
                          </span>
                        )}
                      </div>
                    </>
                  )}

                  {/* Calculated Row Averages */}
                  {serviceType === "ready" && item.avgGrossWt && (
                    <div className="erp-row-averages">
                      <span>Avg Gross: {item.avgGrossWt}g</span>
                      <span>Avg Net: {item.avgNetWt}g</span>
                      {!isPlane && <span>Spec: {item.avgSpecWt}g</span>}
                    </div>
                  )}

                  {/* Remove row button */}
                  {lotSizes.length > 1 && (
                    <button
                      type="button"
                      className="erp-remove-size-btn"
                      onClick={() => handleRemoveSizeRow(index)}
                      title="Remove size"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                className="erp-add-size-btn"
                onClick={handleAddSizeRow}
                disabled={lotSizes.length >= sizeOptions.length}
              >
                <Plus size={16} />
                <span>+ Add More Size</span>
              </button>
            </div>

            {/* Size & Totals Summary Table */}
            <div className="erp-totals-box">
              <div className="erp-totals-title">Size & Totals Summary</div>
              <div className="erp-averages-table-wrap">
                <table className="erp-averages-table">
                  <thead>
                    <tr>
                      <th>Size</th>
                      {serviceType === "ready" && <th>Total Sets</th>}
                      <th>
                        {serviceType === "ready"
                          ? "Total Gross Wt (g)"
                          : "Gross Wt / Set (g)"}
                      </th>
                      <th>
                        {serviceType === "ready"
                          ? "Total Net Wt (g)"
                          : "Net Wt / Set (g)"}
                      </th>
                      {!isPlane && (
                        <th>{product.specification || "Spec"} Wt (g)</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {lotSizes.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <strong>{item.size || "-"}</strong>
                        </td>
                        {serviceType === "ready" && <td>{item.set || "0"}</td>}
                        <td>
                          {serviceType === "ready"
                            ? parseFloat(item.grossWt || 0).toFixed(3)
                            : parseFloat(item.avgGrossWt || 0).toFixed(3)}
                        </td>
                        <td>
                          {serviceType === "ready"
                            ? parseFloat(item.netWt || 0).toFixed(3)
                            : parseFloat(item.avgNetWt || 0).toFixed(3)}
                        </td>
                        {!isPlane && <td>{item.avgSpecWt || "0.000"}</td>}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="erp-totals-row">
                      <td>
                        <strong>Total</strong>
                      </td>
                      {serviceType === "ready" && (
                        <td>
                          <strong>{totals.totalSets}</strong>
                        </td>
                      )}
                      <td>
                        <strong>{totals.totalGrossWt.toFixed(3)}</strong>
                      </td>
                      <td>
                        <strong>{totals.totalNetWt.toFixed(3)}</strong>
                      </td>
                      {!isPlane && (
                        <td>
                          <strong>{totals.totalSpecWt.toFixed(3)}</strong>
                        </td>
                      )}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="erp-modal-footer">
          <button
            type="button"
            className="erp-footer-delete-btn"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isSaving || isDeleting}
          >
            <Trash2 size={16} />
            <span>Delete Product</span>
          </button>

          <div className="erp-footer-right-actions">
            <button
              type="button"
              className="erp-cancel-btn"
              onClick={onClose}
              disabled={isSaving || isDeleting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="erp-submit-btn"
              onClick={handleSaveAndResubmit}
              disabled={isSaving || isDeleting || uploadingImages}
            >
              <Check size={16} />
              <span>{isSaving ? "Submitting..." : "Save & Resubmit QC"}</span>
            </button>
          </div>
        </div>

        {/* Delete Confirmation Popup */}
        {showDeleteConfirm && (
          <div className="erp-confirm-overlay">
            <div className="erp-confirm-dialog">
              <div className="erp-confirm-icon">
                <Trash2 size={24} />
              </div>
              <h4>Delete Rejected Product?</h4>
              <p>
                Are you sure you want to permanently delete{" "}
                <strong>{product.productName}</strong>? This action cannot be
                undone.
              </p>
              <div className="erp-confirm-actions">
                <button
                  type="button"
                  className="erp-confirm-cancel"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  Keep Product
                </button>
                <button
                  type="button"
                  className="erp-confirm-delete"
                  onClick={handleDeleteProduct}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Yes, Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditRejectedProductModal;
